import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Add,
  Download,
  Upload,
  CheckCircle,
  Warning,
  Schedule,
  Description,
  CalendarMonth,
  Euro,
  People,
  Assessment,
  Send,
  Visibility,
  Edit
} from '@mui/icons-material';
import api from '../services/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SodraReports = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState({
    totalEmployees: 0,
    totalGrossSalary: 0,
    totalSodraContributions: 0,
    pendingReports: 0,
    overdueReports: 0
  });

  // SODRA Rates for 2025
  const sodraRates = {
    employer_sodra: 1.77,      // Employer social insurance
    employee_sodra: 19.5,       // Employee social insurance (pension)
    health_insurance: 6.98,     // Health insurance
    guarantee_fund: 0.16,       // Guarantee fund
    unemployment: 1.31,         // Unemployment insurance
    accident_insurance: 0.14,   // Accident insurance
    total_employer: 2.07,       // Total employer contribution
    total_employee: 27.79       // Total employee contribution
  };

  // Report types
  const reportTypes = {
    'SAM': { name: 'SAM - Monthly Insurance Report', deadline: 15 },
    'SD': { name: 'SD - Insurance Declarations', deadline: 15 },
    '1-SD': { name: '1-SD - New Employee Registration', deadline: 'Next day' },
    '2-SD': { name: '2-SD - Employment Termination', deadline: 'Next day' },
    '3-SD': { name: '3-SD - Data Changes', deadline: 'Within 5 days' },
    '12-SD': { name: '12-SD - Annual Declaration', deadline: 'January 31' },
    '13-SD': { name: '13-SD - Benefit Payment', deadline: 'Within 7 days' }
  };

  useEffect(() => {
    fetchReports();
    fetchStatistics();
  }, [selectedMonth, selectedYear]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sodra', {
        params: { month: selectedMonth, year: selectedYear }
      });
      setReports(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError('Failed to load SODRA reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/sodra/statistics', {
        params: { month: selectedMonth, year: selectedYear }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const calculateContributions = (grossSalary) => {
    return {
      employer_sodra: grossSalary * (sodraRates.employer_sodra / 100),
      employee_sodra: grossSalary * (sodraRates.employee_sodra / 100),
      health_insurance: grossSalary * (sodraRates.health_insurance / 100),
      unemployment: grossSalary * (sodraRates.unemployment / 100),
      guarantee_fund: grossSalary * (sodraRates.guarantee_fund / 100),
      accident_insurance: grossSalary * (sodraRates.accident_insurance / 100),
      total_employer: grossSalary * (sodraRates.total_employer / 100),
      total_employee: grossSalary * (sodraRates.total_employee / 100),
      total: grossSalary * ((sodraRates.total_employer + sodraRates.total_employee) / 100)
    };
  };

  const getReportStatus = (report) => {
    if (report.filing_status === 'submitted') {
      return { status: 'Submitted', color: 'success', icon: <CheckCircle /> };
    }
    
    const deadline = new Date(report.submission_deadline);
    const today = new Date();
    
    if (today > deadline) {
      return { status: 'Overdue', color: 'error', icon: <Warning /> };
    }
    
    const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDeadline <= 3) {
      return { status: `Due in ${daysUntilDeadline} days`, color: 'warning', icon: <Schedule /> };
    }
    
    return { status: 'Pending', color: 'info', icon: <Schedule /> };
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const generateSAMReport = async () => {
    try {
      await api.post('/sodra/generate/sam', {
        month: selectedMonth,
        year: selectedYear
      });
      fetchReports();
      alert('SAM report generated successfully!');
    } catch (error) {
      console.error('Error generating SAM report:', error);
      setError('Failed to generate SAM report');
    }
  };

  const getDeadlineDate = (month, year, deadline) => {
    if (typeof deadline === 'number') {
      return new Date(year, month, deadline);
    }
    return null;
  };

  const upcomingDeadlines = [
    {
      report: 'SAM Report',
      deadline: getDeadlineDate(selectedMonth, selectedYear, 15),
      status: 'pending'
    },
    {
      report: 'SD Declaration',
      deadline: getDeadlineDate(selectedMonth, selectedYear, 15),
      status: 'pending'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          SODRA Reports & Compliance
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            size="small"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            {[...Array(12)].map((_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {new Date(2025, i, 1).toLocaleString('default', { month: 'long' })}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            {[2024, 2025, 2026].map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People color="primary" />
                <Box>
                  <Typography variant="h6">{statistics.totalEmployees}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Employees
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Euro color="success" />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.totalGrossSalary)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Gross Salary
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assessment color="info" />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.totalSodraContributions)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Contributions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color={statistics.overdueReports > 0 ? 'error' : 'success'} />
                <Box>
                  <Typography variant="h6">{statistics.overdueReports}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overdue Reports
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Monthly Reports" />
          <Tab label="Employee Declarations" />
          <Tab label="Contribution Calculator" />
          <Tab label="Filing Calendar" />
        </Tabs>
      </Paper>

      {/* Monthly Reports Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Monthly Reports - {new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={generateSAMReport}
            >
              Generate SAM Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              disabled
            >
              Import from EDAS
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Report Number</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Employees</TableCell>
                <TableCell>Gross Salary</TableCell>
                <TableCell>Contributions</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading reports...
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No reports found for this period
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => {
                  const status = getReportStatus(report);
                  return (
                    <TableRow key={report.id} hover>
                      <TableCell>{report.report_number}</TableCell>
                      <TableCell>
                        <Chip label={report.report_type} size="small" />
                      </TableCell>
                      <TableCell>
                        {report.report_month}/{report.report_year}
                      </TableCell>
                      <TableCell>{report.total_employees}</TableCell>
                      <TableCell>{formatCurrency(report.total_gross_salary)}</TableCell>
                      <TableCell>{formatCurrency(report.total_sodra_contributions)}</TableCell>
                      <TableCell>{formatDate(report.submission_deadline)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {status.icon}
                          <Chip 
                            label={status.status}
                            color={status.color}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/sodra/${report.id}`)}
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Download"
                          disabled
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Submit to SODRA"
                          disabled={report.filing_status === 'submitted'}
                        >
                          <Send />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Employee Declarations Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">Employee Declarations (SD Forms)</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/sodra/new-declaration')}
          >
            New Declaration
          </Button>
        </Box>

        <Grid container spacing={3}>
          {Object.entries(reportTypes).map(([code, type]) => (
            <Grid item xs={12} md={6} key={code}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {code}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {type.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Deadline: {type.deadline}
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined">
                      Create
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Contribution Calculator Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>
          SODRA Contribution Calculator
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Calculate Contributions
                </Typography>
                <TextField
                  fullWidth
                  label="Gross Salary (EUR)"
                  type="number"
                  sx={{ mb: 2 }}
                  onChange={(e) => {
                    const salary = parseFloat(e.target.value) || 0;
                    const contributions = calculateContributions(salary);
                    // Update display with calculated values
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Enter gross salary to calculate all contributions
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  2025 SODRA Rates
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Employer Social Insurance"
                      secondary={`${sodraRates.employer_sodra}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Employee Pension Insurance"
                      secondary={`${sodraRates.employee_sodra}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Health Insurance"
                      secondary={`${sodraRates.health_insurance}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Unemployment Insurance"
                      secondary={`${sodraRates.unemployment}%`}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary={<strong>Total Employer</strong>}
                      secondary={<strong>{sodraRates.total_employer}%</strong>}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={<strong>Total Employee</strong>}
                      secondary={<strong>{sodraRates.total_employee}%</strong>}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Minimum Wage Information */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              2025 Lithuania Wage Standards
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Minimum Monthly Wage
                </Typography>
                <Typography variant="h5">€924</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Minimum Hourly Rate
                </Typography>
                <Typography variant="h5">€5.65</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Non-Taxable Income
                </Typography>
                <Typography variant="h5">€747</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Filing Calendar Tab */}
      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom>
          SODRA Filing Calendar
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Monthly Filing Deadlines
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarMonth color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="15th of each month"
                      secondary="SAM - Monthly insurance report for previous month"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarMonth color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="15th of each month"
                      secondary="SD - Insurance declarations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarMonth color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="25th of each month"
                      secondary="Payment deadline for contributions"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Upcoming Deadlines
                </Typography>
                <List>
                  {upcomingDeadlines.map((deadline, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {deadline.status === 'pending' ? <Warning color="warning" /> : <CheckCircle color="success" />}
                      </ListItemIcon>
                      <ListItemText 
                        primary={deadline.report}
                        secondary={deadline.deadline ? formatDate(deadline.deadline) : 'N/A'}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Important Notes */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Important SODRA Filing Notes:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>All reports must be submitted electronically via EDAS system</li>
            <li>Late submissions incur penalties of 0.03% per day</li>
            <li>New employees must be registered before first working day (1-SD)</li>
            <li>Terminations must be reported next working day (2-SD)</li>
            <li>A1 forms for EU postings must be requested separately</li>
          </ul>
        </Alert>
      </TabPanel>
    </Container>
  );
};

export default SodraReports;
